import { useState } from "react";
import type { ProductInfoCreative } from "@/feature/ads/mock/productsInfoCreative.mock";
import { adStylesMock, type AdStyle } from "@/feature/ads/mock/adStyles.mock";
import type { SelectedStyleConfig, Category } from "./adStudio/types";
import { AdStudioHeader } from "./adStudio/AdStudioHeader";
import { AdStudioGallery } from "./adStudio/AdStudioGallery";
import { AdStudioSidebar } from "./adStudio/AdStudioSidebar";
import { AdStudioFooter } from "./adStudio/AdStudioFooter";

interface Step2AdStudioProps {
  product: ProductInfoCreative;
  onClose: () => void;
  onGenerate: (data: {
    product_name: string;
    product_image: string;
    product_description: string;
    user_prompt: string;
    image_template: string;
    format: string;
    config: {
      language: string;
    };
  }[]) => void;
}

export function Step2AdStudio({
  product,
  onClose,
  onGenerate,
}: Step2AdStudioProps) {
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");
  const [selectedStylesConfig, setSelectedStylesConfig] = useState<Map<string, SelectedStyleConfig>>(new Map());
  const [expandedConfigs, setExpandedConfigs] = useState<Set<string>>(new Set());

  const categories = [
    { id: "all" as Category, label: "Todos" },
    { id: "viral" as Category, label: "Viral" },
    { id: "offer" as Category, label: "Oferta" },
    { id: "testimonials" as Category, label: "Testimonios" },
    { id: "custom" as Category, label: "Personalizado" },
  ];

  const currentCategoryData = adStylesMock.find((c) => c.category === selectedCategory);
  const allImages = adStylesMock.find((c) => c.category === "all")?.images || [];
  const displayedImages = selectedCategory === "all" ? allImages : currentCategoryData?.images || [];

  const handleToggleStyle = (style: AdStyle) => {
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(style.id)) {
        newMap.delete(style.id);
        setExpandedConfigs((expanded) => {
          const newExpanded = new Set(expanded);
          newExpanded.delete(style.id);
          return newExpanded;
        });
      } else {
        newMap.set(style.id, {
          style,
          format: "1:1",
          colors: "auto",
          instructions: "",
          quantity: 1,
        });
      }
      return newMap;
    });
  };

  const handleRemoveStyle = (styleId: string) => {
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      newMap.delete(styleId);
      return newMap;
    });
    setExpandedConfigs((expanded) => {
      const newExpanded = new Set(expanded);
      newExpanded.delete(styleId);
      return newExpanded;
    });
  };

  const handleToggleConfig = (styleId: string) => {
    setExpandedConfigs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(styleId)) {
        newSet.delete(styleId);
      } else {
        newSet.add(styleId);
      }
      return newSet;
    });
  };

  const handleUpdateConfig = (styleId: string, updates: Partial<SelectedStyleConfig>) => {
    setSelectedStylesConfig((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(styleId);
      if (current) {
        newMap.set(styleId, { ...current, ...updates });
      }
      return newMap;
    });
  };

  const selectedStyles = Array.from(selectedStylesConfig.values());
  const selectedStyleIds = new Set(selectedStylesConfig.keys());
  const totalImages = selectedStyles.reduce((sum, config) => sum + config.quantity, 0);
  const totalCredits = totalImages * 20; // 20 créditos por imagen
  const canGenerate = selectedStyles.length > 0;

  const handleGenerate = () => {
    const generateData = selectedStyles.flatMap((config) => {
      return Array.from({ length: config.quantity }, () => ({
        product_name: product.name,
        product_image: product.image_url || "",
        product_description: product.description || "",
        user_prompt: config.instructions,
        image_template: config.style.image_url,
        format: config.format,
        config: {
          language: "es",
        },
      }));
    });

    console.log(generateData);
    onGenerate(generateData);
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh]">
      <AdStudioHeader product={product} onClose={onClose} />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <AdStudioGallery
          selectedCategory={selectedCategory}
          displayedImages={displayedImages}
          selectedStyleIds={selectedStyleIds}
          categories={categories}
          onCategoryChange={setSelectedCategory}
          onToggleStyle={handleToggleStyle}
        />

        <AdStudioSidebar
          selectedStyles={selectedStyles}
          expandedConfigs={expandedConfigs}
          onRemoveStyle={handleRemoveStyle}
          onToggleConfig={handleToggleConfig}
          onUpdateConfig={handleUpdateConfig}
        />
      </div>

      <AdStudioFooter
        totalImages={totalImages}
        totalCredits={totalCredits}
        canGenerate={canGenerate}
        onGenerate={handleGenerate}
      />
    </div>
  );
}

